import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Plus, X, AlertCircle } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import api from "../../lib/api";

/**
 * Card de configuración del recordatorio automático al proveedor cuando un
 * pedido lleva X días enviado sin recibirse. Opt-in explícito.
 *
 * Defaults: enabled=false, threshold_days=4, cooldown_days=7, max_reminders=2,
 *          cc_owner=true.
 */
export default function SupplierReminderConfigCard({ restaurantId }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newCc, setNewCc] = useState("");

  const load = async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const cfg = await api.request(`/restaurants/${restaurantId}/supplier-reminder/config`);
      setConfig(cfg);
    } catch {
      toast.error("Error cargando configuración");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [restaurantId]); // eslint-disable-line

  const persist = async (patch) => {
    setSaving(true);
    try {
      const updated = await api.request(`/restaurants/${restaurantId}/supplier-reminder/config`, {
        method: "PUT",
        body: JSON.stringify(patch),
      });
      setConfig(updated);
      toast.success("Guardado");
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = (v) => persist({ enabled: !!v });

  const updateNumber = (field, raw, min, max) => {
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) return;
    persist({ [field]: Math.max(min, Math.min(max, n)) });
  };

  const removeCc = (email) => {
    const next = (config?.extra_cc_emails || []).filter((e) => e !== email);
    persist({ extra_cc_emails: next });
  };

  const addCc = () => {
    const v = (newCc || "").trim();
    if (!v.includes("@")) {
      toast.error("Email inválido");
      return;
    }
    if ((config?.extra_cc_emails || []).includes(v)) {
      setNewCc("");
      return;
    }
    persist({ extra_cc_emails: [...(config?.extra_cc_emails || []), v] });
    setNewCc("");
  };

  if (loading) {
    return (
      <div className="border rounded-xl p-6 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
      </div>
    );
  }

  const enabled = !!config?.enabled;

  return (
    <div className="border rounded-xl p-6 space-y-5" data-testid="supplier-reminder-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
            <Mail className="w-4 h-4" />
          </div>
          <div>
            <h3 className="font-semibold text-base">Recordatorio automático al proveedor</h3>
            <p className="text-sm text-muted-foreground">
              Cuando un pedido lleva varios días enviado sin recibirse, MenuGenius puede mandar
              un email amistoso al proveedor pidiendo confirmación del envío.
            </p>
          </div>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={toggleEnabled}
          disabled={saving}
          data-testid="supplier-reminder-enabled"
        />
      </div>

      {enabled && (
        <div className="space-y-4 pl-13">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="threshold_days" className="text-xs uppercase tracking-wider text-muted-foreground">
                Avisar tras (días)
              </Label>
              <Input
                id="threshold_days"
                type="number"
                min={2}
                max={30}
                defaultValue={config?.threshold_days || 4}
                onBlur={(e) => updateNumber("threshold_days", e.target.value, 2, 30)}
                disabled={saving}
                data-testid="supplier-reminder-threshold"
              />
              <p className="text-xs text-muted-foreground mt-1">2–30 días desde el envío</p>
            </div>
            <div>
              <Label htmlFor="cooldown_days" className="text-xs uppercase tracking-wider text-muted-foreground">
                Cooldown (días)
              </Label>
              <Input
                id="cooldown_days"
                type="number"
                min={2}
                max={30}
                defaultValue={config?.cooldown_days || 7}
                onBlur={(e) => updateNumber("cooldown_days", e.target.value, 2, 30)}
                disabled={saving}
                data-testid="supplier-reminder-cooldown"
              />
              <p className="text-xs text-muted-foreground mt-1">Mínimo entre recordatorios</p>
            </div>
            <div>
              <Label htmlFor="max_reminders" className="text-xs uppercase tracking-wider text-muted-foreground">
                Tope total
              </Label>
              <Input
                id="max_reminders"
                type="number"
                min={1}
                max={5}
                defaultValue={config?.max_reminders || 2}
                onBlur={(e) => updateNumber("max_reminders", e.target.value, 1, 5)}
                disabled={saving}
                data-testid="supplier-reminder-max"
              />
              <p className="text-xs text-muted-foreground mt-1">1–5 emails por pedido</p>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 p-3 border rounded-lg">
            <div>
              <p className="font-medium text-sm">Mandarme una copia (CC)</p>
              <p className="text-xs text-muted-foreground">
                Recibe en tu email cada recordatorio que se envíe al proveedor.
              </p>
            </div>
            <Switch
              checked={!!config?.cc_owner}
              onCheckedChange={(v) => persist({ cc_owner: !!v })}
              disabled={saving}
              data-testid="supplier-reminder-cc-owner"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              Otros destinatarios en CC
            </Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {(config?.extra_cc_emails || []).map((email) => (
                <span
                  key={email}
                  className="inline-flex items-center gap-1.5 bg-muted text-xs px-2.5 py-1 rounded-md"
                  data-testid={`supplier-reminder-cc-${email}`}
                >
                  {email}
                  <button
                    type="button"
                    onClick={() => removeCc(email)}
                    className="text-muted-foreground hover:text-foreground"
                    aria-label={`Quitar ${email}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <Input
                type="email"
                placeholder="contable@ejemplo.com"
                value={newCc}
                onChange={(e) => setNewCc(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") addCc(); }}
                data-testid="supplier-reminder-new-cc"
              />
              <Button type="button" variant="outline" size="sm" onClick={addCc} disabled={saving}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Añadir
              </Button>
            </div>
          </div>

          <div className="flex items-start gap-2 text-xs text-muted-foreground bg-amber-50/40 dark:bg-amber-950/10 rounded-lg p-3">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-amber-700" />
            <span>
              Si el proveedor no tiene email guardado en su ficha, ese pedido se omite
              silenciosamente. El recordatorio sólo se envía a proveedores con email válido.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
